import { DEFAULT_THEME } from '../constants/theme';

/** Applies the 3 source colours (60/30/10 rule) to CSS custom properties. */
export function applyClubTheme(c60, c30, c10) {
  const toRgb = (hex) => {
    const clean = String(hex || '').trim().replace('#', '');
    if (clean.length === 3) {
      const r = parseInt(clean[0] + clean[0], 16);
      const g = parseInt(clean[1] + clean[1], 16);
      const b = parseInt(clean[2] + clean[2], 16);
      return [r, g, b];
    }
    if (clean.length === 6) {
      const r = parseInt(clean.slice(0, 2), 16);
      const g = parseInt(clean.slice(2, 4), 16);
      const b = parseInt(clean.slice(4, 6), 16);
      return [r, g, b];
    }
    return [47, 111, 99];
  };

  const toHex = (r, g, b) => {
    const n = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${n(r)}${n(g)}${n(b)}`;
  };

  const mixHex = (baseHex, mixHexValue, baseWeightPct) => {
    const [r1, g1, b1] = toRgb(baseHex);
    const [r2, g2, b2] = toRgb(mixHexValue);
    const w = Math.max(0, Math.min(100, Number(baseWeightPct) || 0)) / 100;
    return toHex(r1 * w + r2 * (1 - w), g1 * w + g2 * (1 - w), b1 * w + b2 * (1 - w));
  };

  /* ── Contrast-guarded accent derivation ──────────────────────────
     A club picks its colours for a badge, not for text, so the raw value
     is not always readable. Both accents are therefore derived, not used
     directly: each is darkened in small steps until it clears WCAG AA
     (4.5:1) against the surface it will actually sit on.

     For most palettes this returns the source colour untouched. Oratia's
     #069f1a is the case that needs it — it scores only 3.51:1 against
     white, so a white-on-green button using the raw value is unreadable.
  ── */
  const relLuminance = (hex) => {
    const [r, g, b] = toRgb(hex).map((v) => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const contrast = (hexA, hexB) => {
    const a = relLuminance(hexA);
    const b = relLuminance(hexB);
    const [hi, lo] = a > b ? [a, b] : [b, a];
    return (hi + 0.05) / (lo + 0.05);
  };

  const darkenUntilReadable = (hex, against, target = 4.5) => {
    let [r, g, b] = toRgb(hex);
    // 40 steps at 4% is enough to take any colour to near-black
    for (let i = 0; i < 40 && contrast(toHex(r, g, b), against) < target; i++) {
      r *= 0.96; g *= 0.96; b *= 0.96;
    }
    return toHex(r, g, b);
  };

  const theme60 = c60 || DEFAULT_THEME.c60;
  const theme30 = c30 || DEFAULT_THEME.c30;
  const theme10 = c10 || DEFAULT_THEME.c10;

  const root = document.documentElement;
  root.style.setProperty('--club-60', theme60);
  root.style.setProperty('--club-30', theme30);
  root.style.setProperty('--club-10', theme10);

  // Page tint the accents will be read against (matches --club-30-l5 below).
  const pageTint = mixHex(theme30, '#ffffff', 8);

  // Fill: carries white text, so it is measured against white.
  root.style.setProperty('--gs-accent-fill', darkenUntilReadable(theme30, '#ffffff'));
  // Text: sits on the page, so it is measured against the page tint.
  root.style.setProperty('--gs-accent-text', darkenUntilReadable(theme30, pageTint));
  // The 10% colour is the primary action. It is only ever a fill or a large
  // marker, never small text, so only the fill variant is derived.
  root.style.setProperty('--gs-action-fill', darkenUntilReadable(theme10, '#ffffff'));

  // Derived shades for older browsers that do not support CSS color-mix().
  root.style.setProperty('--club-30-l5', mixHex(theme30, '#ffffff', 8));
  root.style.setProperty('--club-30-l4', mixHex(theme30, '#ffffff', 18));
  root.style.setProperty('--club-30-l3', mixHex(theme30, '#ffffff', 32));
  root.style.setProperty('--club-30-l2', mixHex(theme30, '#ffffff', 55));
  root.style.setProperty('--club-30-l1', mixHex(theme30, '#ffffff', 78));
  root.style.setProperty('--club-30-d1', mixHex(theme30, '#000000', 90));
  root.style.setProperty('--club-30-d2', mixHex(theme30, '#000000', 78));
  root.style.setProperty('--club-30-d3', mixHex(theme30, '#000000', 64));
  root.style.setProperty('--club-30-d4', mixHex(theme30, '#000000', 50));
  root.style.setProperty('--club-10-d1', mixHex(theme10, '#000000', 88));
  root.style.setProperty('--club-30-text-strong', mixHex(theme30, '#000000', 85));
}
